import React, { useEffect, useState, useMemo, useRef } from "react";
import MUITable from "./MUI-table/MUI-Table";
import { bindActionCreators } from "redux";
import * as Actions from "../../../scenes/dashboard/components/genomics-dashboard/components/results/components/variant-table/actions";
import { connect } from "react-redux";
import {Accordion, AccordionDetails, Box, CircularProgress} from "@mui/material";
import { ExpandMoreOutlined } from "@mui/icons-material";
import IconLeftExpansionPanelSummary from "./LeftIconExpansion";
import ParticipantHeader from "./ParticipantHeader";
import Typography from "@mui/material/Typography";
/**
 * Build Map<experimentId, CNV count> from CNV aggregation response (nested groups with Experiment_ID and count).
 * When selectedGene is "All" or null, sums counts across all genes per experiment; otherwise only for that gene.
 */
function buildExpToCnvFromAggregations(aggregations, selectedGene) {
    const expToCount = new Map();
    if (!Array.isArray(aggregations)) return expToCount;
    // Treat "All", null, or array of genes (e.g. from "Show Participant Variants" with All) as sum across all genes
    const isAll = selectedGene === "All" || selectedGene == null || selectedGene === "" ||
        (Array.isArray(selectedGene) && selectedGene.length > 0);
    function visit(node) {
        if (!node) return;
        if (Object.prototype.hasOwnProperty.call(node, "Experiment_ID")) {
            const expId = String(node.Experiment_ID);
            const c = Number(node.count) || 0;
            expToCount.set(expId, (expToCount.get(expId) || 0) + c);
            return;
        }
        if (Array.isArray(node.groups)) {
            node.groups.forEach(visit);
        }
    }
    aggregations.forEach((agg) => {
        const gene = agg.Gene_name;
        if (!isAll && gene !== selectedGene) return;
        visit(agg);
    });
    return expToCount;
}

/**
 * Build Map<experimentId, Set<geneName>> from CNV aggregation (for union with SNV genes).
 * Respects selectedGene ("All" vs single gene) same as other aggregation helpers.
 */
function buildExpToGeneSetFromCnvAggregations(aggregations, selectedGene) {
    const expToGeneSet = new Map();
    if (!Array.isArray(aggregations)) return expToGeneSet;
    const isAll = selectedGene === "All" || selectedGene == null || selectedGene === "" ||
        (Array.isArray(selectedGene) && selectedGene.length > 0);
    function visit(node, geneName) {
        if (!node || !geneName) return;
        if (Object.prototype.hasOwnProperty.call(node, "Experiment_ID")) {
            const expId = String(node.Experiment_ID);
            if (!expToGeneSet.has(expId)) expToGeneSet.set(expId, new Set());
            expToGeneSet.get(expId).add(geneName);
            return;
        }
        if (Array.isArray(node.groups)) {
            node.groups.forEach((n) => visit(n, geneName));
        }
    }
    aggregations.forEach((agg) => {
        const gene = agg.Gene_name;
        if (!isAll && gene !== selectedGene) return;
        visit(agg, gene);
    });
    return expToGeneSet;
}

/**
 * Build Map<experimentId, number of genes with CNVs> from same aggregation structure.
 * Used for "Num. of Genes" in Participant table when only CNV results exist (no SNV experimentGeneMap).
 */
function buildExpToNumGenesFromAggregations(aggregations, selectedGene) {
    const expToNumGenes = new Map();
    if (!Array.isArray(aggregations)) return expToNumGenes;
    const isAll = selectedGene === "All" || selectedGene == null || selectedGene === "" ||
        (Array.isArray(selectedGene) && selectedGene.length > 0);
    const seenExp = new Set();
    function visit(node) {
        if (!node) return;
        if (Object.prototype.hasOwnProperty.call(node, "Experiment_ID")) {
            const expId = String(node.Experiment_ID);
            if (!seenExp.has(expId)) {
                seenExp.add(expId);
                expToNumGenes.set(expId, (expToNumGenes.get(expId) || 0) + 1);
            }
            return;
        }
        if (Array.isArray(node.groups)) {
            node.groups.forEach(visit);
        }
    }
    aggregations.forEach((agg) => {
        const gene = agg.Gene_name;
        if (!isAll && gene !== selectedGene) return;
        seenExp.clear();
        visit(agg);
    });
    return expToNumGenes;
}

function ParticipantTable(props){


    const [rows, setRows] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [selectedParticipant, setSelectedParticipant] = useState([]);
    // handle selection variants;
    const [filteredRows, setFilteredRows] = useState([]);

    const [loading, setLoading] = useState(false);

    // Cache last known good n_genes per participant so we don't flicker to 0 when parent sends stale rows (async updateParticipantsData with experimentGeneMap=false)
    const lastNGenesRef = useRef(new Map());

    // Track if user has changed gene selection (selected a specific gene); until then we show "NA" for Num. of Genes on first load (even with CNV)
    const hasUserChangedGeneRef = useRef(false);

    // Clear cache only when query changes (new cohort); do not clear when selectedGeneForVariantView changes so "Show Participant Variants" does not wipe the cache
    useEffect(() => {
        lastNGenesRef.current.clear();
    }, [props.query_id]);

    // New query: show "NA" again on first load for that query
    useEffect(() => {
        hasUserChangedGeneRef.current = false;
        setSelectedParticipant([]);
    }, [props.query_id]);

    // Once user selects a specific gene, we allow showing numbers when they go back to "All"
    useEffect(() => {
        const sv = props.selectedGeneForVariantView;
        const isSingle = typeof sv === "string" && sv !== "" && sv !== "All" && !Array.isArray(sv);
        if (isSingle) hasUserChangedGeneRef.current = true;
    }, [props.selectedGeneForVariantView]);

    const handleFilteredRowsChange = (newFilteredRows) => {
        setFilteredRows(newFilteredRows);
    };


    // Sync table from cohort participants only (do not filter by selected variants)
    useEffect(() => {
        const participantData = props.participants?.participants ?? [];
        setRows(participantData);
        setParticipants(participantData.map(p => p.report_id));
    }, [props.participants]);

    useEffect(() => {
        if (props.participants?.participants) {
          setRows(props.participants.participants);
        }
      }, [props.participants.total]);

    // Build experiment -> SNV count from sample_agg (experimentGeneMap). When selectedGeneForVariantView is "All", null, or array of genes, sum all genes; otherwise use count for that gene only.
    const expToSnv = useMemo(() => {
        const map = new Map();
        const sampleAgg = props.experimentGeneMap;
        if (!Array.isArray(sampleAgg) || sampleAgg.length === 0) return map;
        const selectedGene = props.selectedGeneForVariantView;
        const isAll = selectedGene === "All" || selectedGene == null || selectedGene === "" ||
            (Array.isArray(selectedGene) && selectedGene.length > 0);
        sampleAgg.forEach((item) => {
            const expId = item.experiment;
            if (!expId) return;
            const genes = item.genes || [];
            const total = isAll
                ? genes.reduce((s, g) => s + (Number(g.value) || 0), 0)
                : (genes.find((g) => g.gene === selectedGene)?.value ?? 0);
            map.set(expId, (map.get(expId) || 0) + total);
        });
        return map;
    }, [props.experimentGeneMap, props.selectedGeneForVariantView]);

    // Build experiment -> CNV count from real CNV aggregation when provided (e.g. CNV-only cohort), else fallback to mock.
    const expToCnv = useMemo(() => {
        const aggs = Array.isArray(props.cnvAggregations) ? props.cnvAggregations : [];
        return buildExpToCnvFromAggregations(aggs, props.selectedGeneForVariantView);
    }, [props.cnvAggregations, props.selectedGeneForVariantView]);

    // When only CNV results exist, number of genes per experiment from CNV aggregation (for "Num. of Genes" column).
    const expToNumGenes = useMemo(() => {
        const aggs = (Array.isArray(props.cnvAggregations) && props.cnvAggregations.length > 0)
            ? props.cnvAggregations
            : null;
        return aggs ? buildExpToNumGenesFromAggregations(aggs, props.selectedGeneForVariantView) : new Map();
    }, [props.cnvAggregations, props.selectedGeneForVariantView]);

    // Per-experiment sets of gene names from SNV (for union with CNV when computing "Num. of Genes").
    const expToSnvGeneSet = useMemo(() => {
        const map = new Map();
        const sampleAgg = props.experimentGeneMap;
        if (!Array.isArray(sampleAgg) || sampleAgg.length === 0) return map;
        sampleAgg.forEach((item) => {
            const expId = item.experiment;
            if (!expId) return;
            const genes = (item.genes || []).map((g) => g.gene).filter(Boolean);
            if (genes.length > 0) map.set(expId, new Set(genes));
        });
        return map;
    }, [props.experimentGeneMap]);

    // Per-experiment sets of gene names from CNV aggregation (for union with SNV).
    const expToCnvGeneSet = useMemo(() => {
        const aggs = Array.isArray(props.cnvAggregations) ? props.cnvAggregations : [];
        return buildExpToGeneSetFromCnvAggregations(aggs, props.selectedGeneForVariantView);
    }, [props.cnvAggregations, props.selectedGeneForVariantView]);

    // Num. of Genes is contextual: 1 when a specific gene is selected, total unique genes when "All" is selected.
    const isSingleGeneSelected = useMemo(() => {
        const sv = props.selectedGeneForVariantView;
        return typeof sv === "string" && sv !== "" && sv !== "All" && !Array.isArray(sv);
    }, [props.selectedGeneForVariantView]);

    // Enrich each row with n_snv, n_cnv and n_genes from aggregation data. ExperimentID can be string or array.
    const enrichedRows = useMemo(() => {
        return rows.map((row) => {
            const expIds = Array.isArray(row.ExperimentID)
                ? row.ExperimentID
                : typeof row.ExperimentID === "string"
                    ? row.ExperimentID.split(",").map((s) => s.trim()).filter(Boolean)
                    : [];
            const n_snv = expIds.reduce((sum, id) => sum + (expToSnv.get(id) || 0), 0);
            const n_cnv = expIds.reduce((sum, id) => sum + (expToCnv.get(id) || 0), 0);
            // When a specific gene is selected: show 1 if participant has any variant for that gene, else 0.
            // When "All" is selected: first time (no user gene interaction yet) always show "NA"; else prefer cached value, then row/expToNumGenes.
            const rowKey = row.report_id != null ? String(row.report_id) : (expIds.length > 0 ? expIds.join(",") : null);
            const n_genes = isSingleGeneSelected
                ? ((n_snv > 0 || n_cnv > 0) ? 1 : 0)
                : (() => {
                    if (!hasUserChangedGeneRef.current) return "NA";
                    if (rowKey != null) {
                        const lastGood = lastNGenesRef.current.get(rowKey);
                        if (lastGood != null && lastGood > 0) return lastGood;
                    }
                    // Union of SNV and CNV genes across this row's experiments (e.g. SNV in one gene + CNV in another => 2)
                    const allGenes = new Set();
                    expIds.forEach((id) => {
                        const snvGenes = expToSnvGeneSet.get(id);
                        if (snvGenes) snvGenes.forEach((g) => allGenes.add(g));
                        const cnvGenes = expToCnvGeneSet.get(id);
                        if (cnvGenes) cnvGenes.forEach((g) => allGenes.add(g));
                    });
                    const candidate = allGenes.size;
                    if (rowKey != null && candidate > 0) {
                        lastNGenesRef.current.set(rowKey, candidate);
                        return candidate;
                    }
                    if (candidate === 0 && (rowKey == null || lastNGenesRef.current.get(rowKey) == null)) {
                        return "NA";
                    }
                    return candidate;
                })();
            return {
                ...row,
                n_snv,
                n_cnv,
                n_genes
            };
        });
    }, [rows, expToSnv, expToCnv, expToSnvGeneSet, expToCnvGeneSet, isSingleGeneSelected]);

    // Only show participants that have at least one variant (SNV or CNV) for the current selection; update count accordingly.
    const rowsWithVariants = useMemo(() => {
        return enrichedRows.filter((row) => (row.n_snv || 0) > 0 || (row.n_cnv || 0) > 0);
    }, [enrichedRows]);

      const variantExperiments = new Set(props.sampleList || []);
      const phenostoreExperiments = new Set((props.participants.participants || []).map(p => p.ExperimentID));

      const compareSets = (setA, setB) => {
        if (setA.size !== setB.size) return false;
        for (const item of setA) {
          if (!setB.has(item)) {
            return false;
          }
        }
        return true;
      };
      
      const setsAreEqual = compareSets(variantExperiments, phenostoreExperiments);
      
    
    const handleSelectedParticipant = (selected, fromParticipantTable, isAffected, isSelected, runQuery, queryMeta) => {

        if (fromParticipantTable) {
            props.actions.selectedVariant([]);
        }

        const shouldRunQuery = runQuery === true;

        if (selected === "ALL_SAMPLES") {
            props.selectParticipants("ALL_SAMPLES", fromParticipantTable, "experiments", shouldRunQuery, queryMeta);
            if (fromParticipantTable) setSelectedParticipant([]);
            return;
        }

        if(!isAffected && isSelected ) {
            props.selectParticipants(selected, fromParticipantTable, "participants", shouldRunQuery, queryMeta);
        }
        else if(!isAffected && !isSelected) {
            props.selectParticipants(props.participants.experiments, fromParticipantTable, "experiments", shouldRunQuery, queryMeta);
        }
        else if(isAffected && !isSelected) {
            props.selectParticipants(selected, fromParticipantTable, "participants", shouldRunQuery, queryMeta);
        }
        else if(isAffected && isSelected) {
            props.selectParticipants(selected, fromParticipantTable, "participants", shouldRunQuery, queryMeta);
        }

        if(fromParticipantTable){
            setSelectedParticipant(selected);
        }
        else{
            setSelectedParticipant([])
        }

    };

    const getTableTitleText = () => {

        return <ParticipantHeader total={props.participants.total}
                                  loadingParticipants={props.loadingParticipants || loading}
                                  rowsL={rowsWithVariants.length}
                                  setsAreEqual= {setsAreEqual}
                                  />
    }


    const accordionHeader= {backgroundColor: "#f0f0f0", width: "100%"};

    return <Box p={1} style={{float: "left", width: "100%"}}>
        <Accordion defaultExpanded={false}>
            <IconLeftExpansionPanelSummary
                style={accordionHeader}
                expandIcon={<ExpandMoreOutlined />}
                IconButtonProps={{edge: 'start'}}
                aria-controls="panel1a-content"
                id="panel1a-header">
                    {getTableTitleText()}
            </IconLeftExpansionPanelSummary>
            <AccordionDetails>
                {(props.loadingParticipants || loading )
                    ?  <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        minHeight="200px" // or any height to define the center area
                    >
                        <CircularProgress />
                        <Typography variant="body2" mt={2}> {/* Adjust typography variant as needed */}
                            Loading table data, please wait a moment.
                        </Typography>
                    </Box>
                    : <MUITable
                        loadingParticipants = {props.loadingParticipants}
                        table_page={1}
                        selectParticipants = {handleSelectedParticipant}
                        loadMore= {function(d){console.log("xxx")}}
                        total_participants={rowsWithVariants.length}
                        rows={rowsWithVariants}
                        participantsIDs = {participants}
                        experiments={props.sampleList}
                        total={props.participants.total}
                        aggregations={props.participants.aggregations}
                        query_id={props.query_id}
                        onFilteredRowsChange={handleFilteredRowsChange} 
                    />
                }
            </AccordionDetails>
        </Accordion>
    </Box>




}



function mapStateToProps (state) {
    if(state.authorization!==undefined)
    {
        return {
            token: state.authorization.token
        }
    }
    else{
        return {snv: []}
    }
}

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(Actions, dispatch)
});



//export default Variant_Table


export default connect(mapStateToProps,mapDispatchToProps)(ParticipantTable);